import React from 'react';
import { get } from 'lodash-es';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { CmsModel, CmsPaths } from 'client/data/models/cms';
import { getTreehouseEligiblePath, TreehouseModel } from 'client/data/models/treehouse';
import { TypeRankingsModel, buildTypeRankingsPath } from 'client/data/models/type-rankings';
import { EditorialModel, buildTypeArticlesPath } from 'client/data/models/editorial';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { parseContent } from 'client/data/cms/content';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { setCriticalCss } from 'client/actions/critical-css';
import { updateVehicle } from 'client/actions/vehicle';
import { handle404Error } from 'site-modules/shared/utils/errors';
import { isBestContent } from 'site-modules/editorial/utils/best';
import { getArticleCriticalCssKeys } from 'site-modules/editorial/utils/article-critical-css-keys';
import { getTypeArticleConfigPath, getTypeArticleContentPath } from 'site-modules/editorial/utils/content-path';
import { getBestSeoObject } from 'site-modules/editorial/pages/best/seo';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
} from 'site-modules/shared/components/native-ad/utils/utils';
import {
  CAR_NEWS_LANDING_CONTENT_PATH,
  EV_NEWS_WIDGET_CONTENT_PATH,
} from 'site-modules/shared/constants/editorial/editorial';
import { ENTRY_CONTENT, ENTRY_RENDER_TYPE } from 'site-modules/editorial/constants/entry-render';
import { getRankingsContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { CAR_TYPES, PAGE_CAR_TYPES } from 'site-modules/shared/constants/upper-funnel/car-types';
import { isEvArticle, TREEHOUSE_ARTICLE_ID } from 'site-modules/editorial/utils/ev';

import { getSeoObject } from './seo';

export function getPageName(slug, prefix) {
  return `${prefix ? `${prefix}_` : ''}advice_${slug.replace(/-/g, '_')}_article`;
}

export function getPageCategory() {
  return 'advice';
}

export const getPageOptions = ({ type, articleId, pageTemplate: PageTemplate, criticalCssTemplateKey, chunkName }) => ({
  name: getPageName(type),
  category: getPageCategory(),
  chunkName,
  adsCustomTargeting: {
    sect1: (articleId || '').replace(/[^a-zA-Z0-9]/g, ''),
    sect2: 'articles',
  },
  seo: (state, props) => {
    const contentPath = getTypeArticleContentPath(type, articleId);
    const content = parseContent(state.cms.content[contentPath]);

    return isBestContent(content.id)
      ? getBestSeoObject(content, props.location.pathname)
      : getSeoObject(state, contentPath, getTypeArticleConfigPath(type), props.location.pathname);
  },
  preload: store => {
    const preloader = new ModelPreloader(store);
    const articlePath = getTypeArticleContentPath(type, articleId);
    preloader.resolve(`content["${articlePath}"]`, CmsModel);

    const state = store.getState();
    const { mobile } = state;

    return preloader.load().then(modelState => {
      const article = modelState.get(`content["${articlePath}"]`, CmsModel);
      let pageName = getPageName(type);

      if (!(article && article.id)) {
        throw handle404Error({ errorMessage: `No content was found for ${articlePath}` });
      }
      const edwPrefix = get(article, 'contentMetadata.edwPrefix');
      if (edwPrefix) {
        pageName = getPageName(type, article.contentMetadata.edwPrefix);
        store.dispatch(
          legacyPageUpdate({
            pageName,
          })
        );
      }

      const isBestContentId = isBestContent(article.id);

      if (
        TREEHOUSE_ARTICLE_ID.includes(articleId) ||
        (!isBestContentId && isEvArticle({ slug: type, categories: article.categories }))
      ) {
        preloader.resolve(getTreehouseEligiblePath(), TreehouseModel);
      }

      if (!isBestContentId) {
        preloader.resolve(`content["${getTypeArticleConfigPath(type)}"]`, CmsModel);
        preloader.resolve(CmsPaths.buildAllAuthorsPath({ contentPath: articlePath }), CmsModel);
      } else {
        preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
      }

      const vehicle = { type: { niceName: type } };
      store.dispatch(updateVehicle({ type: { niceName: type } }));

      if (isBestContentId) {
        setAdsListToPreload(preloader, store.getState(), [SEGMENT_LISTING_AD.AD_NAME], vehicle);
      }

      const hasCarNewsModules = parseContent(article)
        .child(ENTRY_CONTENT.MAIN)
        .children()
        .some(entry =>
          [ENTRY_RENDER_TYPE.NEWS_MODULE, ENTRY_RENDER_TYPE.CAR_NEWS_MODULE].includes(
            entry.metadata('renderType').value()
          )
        );
      if (hasCarNewsModules) {
        preloader.resolve(`content["${CAR_NEWS_LANDING_CONTENT_PATH}"]`, CmsModel);
      }

      const hasEvNewsModule = parseContent(article)
        .child(ENTRY_CONTENT.MAIN)
        .children()
        .some(entry => [ENTRY_RENDER_TYPE.EV_NEWS_MODULE].includes(entry.metadata('renderType').value()));
      if (hasEvNewsModule) {
        preloader.resolve(`content["${EV_NEWS_WIDGET_CONTENT_PATH}"]`, CmsModel);
      }

      const hasTypeNewsModule = parseContent(article)
        .child(ENTRY_CONTENT.MAIN)
        .children()
        .some(entry => [ENTRY_RENDER_TYPE.TYPE_NEWS_MODULE].includes(entry.metadata('renderType').value()));
      if (hasTypeNewsModule) {
        preloader.resolve(buildTypeArticlesPath(type), EditorialModel);
      }

      const hasVideoModule = parseContent(article)
        .child(ENTRY_CONTENT.MAIN)
        .children()
        .some(entry => [ENTRY_RENDER_TYPE.VIDEOS].includes(entry.metadata('renderType').value()));
      if (hasVideoModule && Object.values(PAGE_CAR_TYPES).includes(type)) {
        preloader.resolve(`content["${getRankingsContentPath(type)}"]`, CmsModel);
      }

      const hasEvRangeTable = articleId === 'ev-buying-guide';
      if (hasEvRangeTable) {
        preloader.resolve(buildTypeRankingsPath(CAR_TYPES.ELECTRIC), TypeRankingsModel);
      }

      return preloader.load().then(modelStateNext => {
        const siteServedAds = modelStateNext.get(
          getCreativeConfigAdPathByVehicle(mobile, vehicle),
          NativeAdsCreativeConfigModel
        );

        store.dispatch(
          setCriticalCss({
            ...getArticleCriticalCssKeys(criticalCssTemplateKey, article, { siteServedAds, hasEvRangeTable }),
          })
        );

        return preloader.load();
      });
    });
  },
  component: props => {
    const typeFromInput = type ?? props.match.params.type;
    const articleIdFromInput = articleId ?? props.match.params.articleId;
    return <PageTemplate type={typeFromInput} articleId={articleIdFromInput} {...props} />;
  },
  options: {
    hasVehicleContext: true,
  },
});
